import { ControlledTextInput } from '@components/ControlledTextInput';
import { CountrySelector } from '@components/CountrySelector';
import { STRUCTURED_ONLY_FIELDS } from '@pages/payments/utils/addressUtils';
import { useEffect } from 'react';
import type { FieldValues, Path } from 'react-hook-form';
import { Controller, get, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type UnstructuredAddressInputProps<T extends FieldValues> = {
	prefix: Path<T>;
	translatePrefix: string;
	required?: boolean;
	disabled?: boolean;
	requiredIfSelection?: boolean;
	lineLabelKey?: string;
};

const buildFieldPath = <T extends FieldValues>(prefix: Path<T>, name: string) =>
	`${prefix}.${name}` as Path<T>;

export const UnstructuredAddressInput = <T extends FieldValues>({
	prefix,
	translatePrefix,
	disabled,
	required,
	requiredIfSelection,
	lineLabelKey = 'line',
}: UnstructuredAddressInputProps<T>) => {
	const { control, watch, formState, setValue, clearErrors, getValues } =
		useFormContext<T>();
	const { t } = useTranslation('', { keyPrefix: translatePrefix });
	const { t: tCommon } = useTranslation();
	const errors = get(formState.errors, prefix);

	const fieldPath = (name: string) => buildFieldPath<T>(prefix, name);
	const setAddressFieldValue = (name: string, value: unknown, dirty = false) =>
		setValue(fieldPath(name), value as T[Path<T>], {
			shouldValidate: false,
			shouldDirty: dirty,
			shouldTouch: dirty,
		});

	const isCountrySelected = !!watch(fieldPath('country'));
	const isLineRequired = required || (requiredIfSelection && isCountrySelected);
	const requiredMessage = tCommon('payments.newPayment.fieldIsRequired');

	useEffect(() => {
		setAddressFieldValue('type', 'UNSTRUCTURED');

		const address = getValues(prefix as Path<T>) as
			| Record<string, string | null | undefined>
			| undefined;
		if (address && !address.line) {
			const line = STRUCTURED_ONLY_FIELDS.map((name) => address[name])
				.filter(Boolean)
				.join(' ');
			if (line) {
				setAddressFieldValue('line', line);
			}
		}

		STRUCTURED_ONLY_FIELDS.forEach((name) => {
			setAddressFieldValue(name, undefined);
			clearErrors(fieldPath(name));
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [prefix, setValue, clearErrors, getValues]);

	const handleCountryChange = (country?: string) => {
		if (country) {
			setAddressFieldValue('country', country);
			return;
		}
		clearErrors(prefix);
		setAddressFieldValue('country', null, true);
		setAddressFieldValue('line', null, true);
		setAddressFieldValue('type', undefined, true);
	};

	return (
		<div className="space-y-space-16" data-test-id={prefix}>
			<Controller
				control={control}
				name={fieldPath('country')}
				render={({ field: { onChange: _, ...countryField } }) => (
					<CountrySelector
						{...countryField}
						className="max-w-125"
						error={errors?.country?.message}
						isDisabled={disabled}
						isRequired
						label={t('country')}
						onChange={handleCountryChange}
						showClear
					/>
				)}
				rules={{ required: requiredMessage }}
			/>
			{isCountrySelected && (
				<ControlledTextInput
					disabled={disabled}
					label={t(lineLabelKey)}
					name={fieldPath('line')}
					rules={{
						required: isLineRequired && requiredMessage,
					}}
				/>
			)}
		</div>
	);
};
